// CommodityCardsEditor.jsx — the "expanded commodity cards" editor from the PID tables
// plan (decision 3a): one bordered card per commodity; styles as removable chips with an
// add-select; on hasSizes (Methods) each style nests a per-style size row. Operates on the
// commodityGroups shape the Method panels have always saved from:
//   [{ commodity: {value,label}, styleGroups: [{ style: {value,label}, sizes: [{value,label}] }] }]
// (Labels uses the same shape with sizes unused.)
import React from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import EditableComboSelect from './EditableComboSelect';

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Card = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--green-a15);
  border-radius: 12px;
  padding: 14px 16px;
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
`;

const CardName = styled.span`
  font-weight: 700;
  color: var(--green-800);
  font-size: 0.98rem;
`;

const RemoveCardBtn = styled.button`
  border: none;
  background: none;
  color: var(--ink-a45);
  cursor: pointer;
  line-height: 0;
  padding: 5px;
  border-radius: 7px;

  &:hover { color: var(--color-danger); background: var(--danger-a10); }
  &:focus-visible { outline: 2px solid var(--green-a45); }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const RowLabel = styled.span`
  flex: 0 0 52px;
  font-size: 0.64rem;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--green-700);
`;

const SizesRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 8px 0 0 14px;
  padding-left: 12px;
  border-left: 2px solid var(--green-a15);
`;

const StyleName = styled.span`
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--ink-a70);
  min-width: 70px;
`;

const ChipX = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--green-a60);
  background: var(--green-a15);
  color: var(--green-800);
  font: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 3px 6px 3px 10px;
  border-radius: 7px;
  cursor: pointer;

  span { line-height: 0; opacity: 0.55; }

  &:hover {
    background: var(--danger-a10);
    border-color: var(--danger-a30);
    color: var(--color-danger);
  }
  &:hover span { opacity: 1; }
  &:focus-visible { outline: 2px solid var(--green-a45); outline-offset: 1px; }
`;

const AddRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const removableChip = (label, onRemove, ariaLabel) => (
  <ChipX type="button" key={ariaLabel} aria-label={ariaLabel} onClick={onRemove}>
    {label}
    <span><X size={11} /></span>
  </ChipX>
);

// Type-in-the-control combobox add-picker (PID tables UX plan, M7). Same signature as
// the retired native <select> helper: onPick still receives the option value as a string.
const addSelect = (placeholder, options, onPick, ariaLabel) => (
  <EditableComboSelect
    placeholder={placeholder}
    options={options}
    ariaLabel={ariaLabel || placeholder}
    onChange={(v) => onPick(String(v))}
  />
);

const CommodityCardsEditor = ({
  groups,
  onChange,
  commodityOptions,
  styleOptions,
  sizeOptionsFor,
  hasSizes = false,
}) => {
  const usedCommodities = new Set(groups.map((g) => String(g.commodity?.value)));

  const update = (mutate) => {
    const next = groups.map((g) => ({
      ...g,
      styleGroups: (g.styleGroups || []).map((sg) => ({ ...sg, sizes: [...(sg.sizes || [])] })),
    }));
    mutate(next);
    onChange(next);
  };

  return (
    <Cards>
      {groups.map((group, gi) => {
        const usedStyles = new Set((group.styleGroups || []).map((sg) => String(sg.style?.value)));
        const stylePool = styleOptions.filter((o) => !usedStyles.has(String(o.value)));
        const sizePool = hasSizes ? sizeOptionsFor(group.commodity?.value) : [];

        return (
          <Card key={`${group.commodity?.value}-${gi}`}>
            <CardHead>
              <CardName>{group.commodity?.label}</CardName>
              <RemoveCardBtn
                type="button"
                aria-label={`Remove commodity ${group.commodity?.label}`}
                onClick={() => update((next) => next.splice(gi, 1))}
              >
                <X size={15} />
              </RemoveCardBtn>
            </CardHead>

            <Row>
              <RowLabel>Styles</RowLabel>
              {(group.styleGroups || []).map((sg, si) =>
                removableChip(
                  sg.style?.label,
                  () => update((next) => next[gi].styleGroups.splice(si, 1)),
                  `Remove style ${sg.style?.label} from ${group.commodity?.label}`
                ))}
              {addSelect('Add style…', stylePool, (value) => {
                const opt = styleOptions.find((o) => String(o.value) === value);
                if (opt) update((next) => next[gi].styleGroups.push({ style: opt, sizes: [] }));
              }, `Add style to ${group.commodity?.label}`)}
            </Row>

            {hasSizes && (group.styleGroups || []).map((sg, si) => {
              const usedSizes = new Set((sg.sizes || []).map((s) => String(s.value)));
              const pool = sizePool.filter((o) => !usedSizes.has(String(o.value)));
              return (
                <SizesRow key={`${sg.style?.value}-${si}`}>
                  <StyleName>{sg.style?.label}</StyleName>
                  {(sg.sizes || []).map((s, zi) =>
                    removableChip(
                      s.label,
                      () => update((next) => next[gi].styleGroups[si].sizes.splice(zi, 1)),
                      `Remove size ${s.label} from ${sg.style?.label}`
                    ))}
                  {addSelect('Add size…', pool, (value) => {
                    const opt = sizePool.find((o) => String(o.value) === value);
                    if (opt) update((next) => next[gi].styleGroups[si].sizes.push(opt));
                  }, `Add size to ${sg.style?.label}`)}
                </SizesRow>
              );
            })}
          </Card>
        );
      })}

      <AddRow>
        {addSelect(
          'Add commodity…',
          commodityOptions.filter((o) => !usedCommodities.has(String(o.value))),
          (value) => {
            const opt = commodityOptions.find((o) => String(o.value) === value);
            if (opt) onChange([...groups, { commodity: opt, styleGroups: [] }]);
          },
          'Add commodity'
        )}
      </AddRow>
    </Cards>
  );
};

export default CommodityCardsEditor;
